import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,

} from "@mui/material";
// components
import { Log, sendDataWithPost } from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import Pagination from "../../components/Pagination";
import { acquirerList } from "../../utils/constants";

// ----------------------------------------------------------------------

const defaultValue = {}

const TABLE_HEAD = [
  { id: "merchant_id", label: "Merchant ID" },
  { id: "merchant_name", label: "Merchant Name" },
  { id: "account_number", label: "Account Number" },
  { id: "txn_fee_fixed", label: "Txn Fee Fixed" },
  { id: "payout_cycle", label: "Payout Cycle" },
  { id: "bank", label: "Bank" },
  { id: "bank_address", label: "Bank Address" },
  { id: "bic_swift", label: "Bic Swift" },
  { id: "acquirer", label: "Acquirer" },
  { id: "source", label: "Source" },
];

export default function IngestMerchantInfo() {
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState({});

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryList({
      page: newPage + 1,
      page_size: pagination.page_size,
    }, filter);
  };

  const handleChangeRowsPerPage = (event) => {
    queryList({
      page: 1,
      page_size: event.target.value,
    }, filter);
  };

  const queryList = (params, filter) => {
    loaded && setLoaded(false);
    queryPage(params, filter);
  };

  const queryPage = (params, filter) => {
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/payout/ingestor/filter_merchant_info/?${queryParams}`;
    sendDataWithPost(url, filter, setTableData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = (fParams) => {
    const newFParams = { ...fParams, acquirer: fParams.acquirer?.id, merchant_id: fParams.merchant_id?.trim(), merchant_name: fParams.merchant_name?.trim(), account_number: fParams.account_number?.trim() };
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newFParams.payout_cycle === 'all') delete newFParams.payout_cycle;
    if (newFParams.source === 'all') delete newFParams.source;
    Log.info("Search Payout Merchant Info list", newFParams);
    setFilter(newFParams);
    queryList(newPagination, newFParams);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "text",
      label: "Merchant ID",
      param: "merchant_id",
    },
    {
      type: "text",
      label: "Merchant Name",
      param: "merchant_name",
    },
    {
      type: "text",
      label: "Account Number",
      param: "account_number",
    },
    {
      type: "select",
      label: "Payout Cycle",
      param: "payout_cycle",
      options: [
        { name: "All", id: "all" },
        { name: "T+2", id: "TPLUS2" },
        { name: "MONDAY", id: 'MON' },
        { name: "FRIDAY", id: "FRI" },
        { name: "WEDNESDAY FRIDAY", id: "WEDFRI" },
        { name: "MONTHLY", id: "MONTHLY" },
        { name: "NO SETTLEMENT", id: "NO_SETTLEMENT" },
      ]
    },
    {
      type: "search-select",
      label: "Acquirer",
      param: "acquirer",
      options: acquirerList.map(item => ({
        id: item.id,
        label: item.name
      }))
    },
    {
      type: "select",
      label: "Source",
      param: "source",
      options: [
        { name: "All", id: "all" },
        { name: "GW2", id: "gw2" },
        { name: "GW3", id: 'gw3' },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={true}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      merchant_id,
                      merchant_name,
                      account_number,
                      txn_fee_fixed,
                      payout_cycle,
                      acquirer,
                      source,
                      bank,
                      bank_address,
                      bic_swift_code,
                    } = row;

                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={merchant_id} />
                        <CustomTableCell field={merchant_name} />
                        <CustomTableCell field={account_number} />
                        <CustomTableCell field={txn_fee_fixed} />
                        <CustomTableCell field={payout_cycle} />
                        <CustomTableCell field={bank} />
                        <CustomTableCell field={bank_address} />
                        <CustomTableCell field={bic_swift_code} />
                        <CustomTableCell field={acquirer} />
                        <CustomTableCell field={source?.toUpperCase()} />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
}
